import { useEffect, useRef } from "react";
import { useConnect } from "wagmi";
import { toast } from "sonner";
import { friendlyWalletMessage, reportWalletError } from "@/lib/wallet-diagnostics";

/**
 * Listens for wagmi connector errors (raised when a user selects a wallet
 * inside the AppKit modal and the connect flow fails) and surfaces a
 * user-friendly toast including the wallet name plus the underlying error
 * details. Also logs the diagnostic to the console and session storage via
 * `reportWalletError` for post-mortem debugging.
 */
export function WalletErrorListener() {
  const { error, variables, status, connectors } = useConnect();
  const lastKey = useRef<string | null>(null);

  useEffect(() => {
    if (status !== "error" || !error) return;
    const connector =
      variables?.connector && typeof variables.connector === "object"
        ? (variables.connector as { name?: string; id?: string })
        : undefined;
    const walletName =
      connector?.name ??
      connectors.find((c) => c.id === (connector as { id?: string } | undefined)?.id)?.name ??
      "Wallet";

    const key = `${walletName}:${error.message}:${(error as { code?: string | number }).code ?? ""}`;
    if (lastKey.current === key) return;
    lastKey.current = key;

    reportWalletError(error, { walletName, context: "wagmi.connect" });
    const friendly = friendlyWalletMessage(error, walletName);
    toast.error(friendly, {
      description: error.message,
      duration: 12000,
    });
  }, [error, status, variables, connectors]);

  return null;
}
